import type { TFunction } from 'i18next'

import {
  ORDER_MARK_AS_STATUSES,
  describeVehicles,
  formatShipmentStatus,
  orderMarkAsLabelKey,
  pickRouteEndpoints,
} from '#/entities/shipment'
import type { OrderMarkAsStatus, ShipmentStage } from '#/entities/shipment'
import { ShipmentStatus } from '#/shared/api/generated/shipment/models'
import type { ShipmentListItem } from '#/shared/api/generated/shipment/models'

import type { ActiveLoad } from '../model/conversation'
import type { AvatarTone } from '../model/types'

/** In-progress statuses read as amber; finished green; problems red; archived is
 *  muted so it doesn't compete with live loads; rest blue. */
function toneForStatus(status: string): AvatarTone {
  switch (status) {
    case ShipmentStatus.completed:
      return 'green'
    case ShipmentStatus.picked_up:
    case ShipmentStatus.dispatched:
    case ShipmentStatus.ongoing:
      return 'amber'
    case ShipmentStatus.issue:
    case ShipmentStatus.on_hold:
      return 'red'
    case ShipmentStatus.archived:
      return 'gray'
    default:
      return 'blue'
  }
}

function isOrderStatus(status: string): status is OrderMarkAsStatus {
  return (ORDER_MARK_AS_STATUSES as ReadonlyArray<string>).includes(status)
}

/** Translated tag label — order statuses get their toolbar label, else formatted. */
function labelForStatus(status: string, t: TFunction): string {
  return isOrderStatus(status)
    ? t(orderMarkAsLabelKey(status))
    : formatShipmentStatus(status)
}

/** Origin → destination as bare state codes, e.g. `NY–CA`. */
function routeStates(shipment: ShipmentListItem): string {
  const { origin, destination } = pickRouteEndpoints(shipment.stops)
  return [origin?.state, destination?.state].filter(Boolean).join('–')
}

/**
 * Shape one stage's shipments into header strip rows, tagging each with its
 * stage so the row opens the right lead/quote/order detail. Every status is
 * shown, archived included — on a call the agent needs the customer's whole
 * history, and an archived row carries its own muted "Archived" tag.
 */
export function mapActiveLoads(
  shipments: ReadonlyArray<ShipmentListItem>,
  stage: ShipmentStage,
  t: TFunction,
): ActiveLoad[] {
  return shipments.map((shipment) => ({
    id: shipment.id,
    stage,
    code: shipment.code,
    summary: [describeVehicles(shipment.vehicles).name, routeStates(shipment)]
      .filter(Boolean)
      .join(' · '),
    statusLabel: labelForStatus(shipment.status, t),
    tone: toneForStatus(shipment.status),
  }))
}
